import React, { useEffect, useRef, useState } from "react";
import { Rnd } from "react-rnd";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Document, Page, pdfjs } from "react-pdf";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaVenusMars,
  FaHeading,
  FaCircle,
  FaSearch,
  FaCog,
  FaTimes,
  FaSignature,
  FaCalendarWeek,
  FaEdit,
  FaCodepen,
} from "react-icons/fa";
import { IoIosCloseCircle } from "react-icons/io";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useCreateTemplateFieldMutation } from "../services/useCreateTemplateFieldMutation";
import { toast } from "react-toastify";
import pdfjsWorker from "react-pdf/dist/esm/pdf.worker.entry";
import { useGetAllStandardFields } from "../services/useGetAllStandardFields";
import Spinner from "../components/Spinner";
import Modal from "../components/Modal";
import CustomInput from "../components/Input";
import CustomButton from "../components/Button";
import { useCreateNewField } from "../services/useCreateNewField";
import { useGetStandardFieldById } from "../services/useGetStandardFieldById";
import { useUpdateField } from "../services/useUpdateField";
import { useDeleteField } from "../services/useDeleteField";
import { useGetTemplateDetails } from "../services/useGetTemplateDetails";
import { useUpdateTemplateFieldsMutation } from "../services/useUpdateFieldsMutation";
import { CP_LABEL, cpEditorLabel, cpSignerForOrder } from "./cpFieldRules";

pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const iconMap = {
  name: <FaUser />,
  email: <FaEnvelope />,
  title: <FaHeading />,
  phone: <FaPhone />,
  address: <FaMapMarkerAlt />,
  date: <FaCalendarWeek />,
  gender: <FaVenusMars />,
  signature: <FaSignature />,
  country: <FaMapMarkerAlt />,
};

const fieldInitialValues = {
  field_name: "",
  field_type: "text",
  default_width: "85",
  default_height: "20",
};

const inputTypes = [
  "text",
  "email",
  "number",
  "date",
  "gender",
  "phone",
  "signature",
];

function capitalize(str) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

const DraggableField = ({ field, setEditFieldId, handleDelete, cpLabel }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: "field",
    item: { type: field.field_type, label: capitalize(field.field_name) },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  return (
    <div
      ref={drag}
      className={`flex items-center gap-2 py-2 pl-4 cursor-pointer hover:bg-gray-100 transition-opacity duration-200 rounded-md ${
        isDragging ? "opacity-50" : "opacity-100"
      }`}
    >
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center h-6 w-6 rounded-md border border-solid border-yellow-900 bg-yellow-500">
            <span className="text-sm mt-0.5 w-6 h-6 flex items-center justify-center">
              {iconMap[field.field_name.toLowerCase()] || <FaCodepen />}
            </span>
          </div>
          <div className="flex flex-col items-start">
            <h3 className="text-sm font-medium capitalize text-gray-800">
              {field.field_name}
            </h3>
            {cpLabel && (
              <span
                title={cpLabel.hint}
                className={`text-xs ${cpLabel.text === CP_LABEL.auto ? "text-green-700" : "text-gray-500"}`}
              >
                {cpLabel.text}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const DocumentCanvas = ({
  fields,
  setFields,
  setSelectedField,
  link,
  selectedUser,
  isOverlapping,
  allFields,
}) => {
  const [numPages, setNumPages] = useState(null);
  const selectedUserRef = useRef(selectedUser);

  useEffect(() => {
    selectedUserRef.current = selectedUser;
  }, [selectedUser]);

  const [, drop] = useDrop(() => ({
    accept: "field",
    drop: (item, monitor) => {
      const delta = monitor.getSourceClientOffset();
      if (!delta) return;

      const canvas = document.getElementById("doc-canvas");
      const canvasRect = canvas.getBoundingClientRect();

      const getFieldData = allFields.find(
        (field) => field.field_name.toLowerCase() === item.label.toLowerCase()
      );

      const fieldWidth = getFieldData?.default_width;
      const fieldHeight = getFieldData?.default_height;
      const fieldId = getFieldData?.id;

      const pageElement = document
        .elementFromPoint(delta.x, delta.y)
        ?.closest(".react-pdf__Page");

      if (!pageElement) return;

      const pageNumber = Number(pageElement.dataset.pageNumber);
      const pageRect = pageElement.getBoundingClientRect();

      const adjustedX = delta.x - pageRect.left;
      const adjustedY = delta.y - pageRect.top;

      const newField = {
        id: new Date().getTime(),
        ...item,
        field_id: fieldId,
        x: adjustedX,
        y: adjustedY,
        width: fieldWidth || 200,
        height: fieldHeight || 50,
        page: pageNumber,
        color: selectedUserRef.current?.color || "blue",
        signing_order: selectedUserRef.current?.signing_order,
      };

      setFields((prev) => [...prev, newField]);
      setSelectedField(newField);
    },
  }));

  const updateField = (id, updates) => {
    setFields((prev) =>
      prev.map((field) => (field.id === id ? { ...field, ...updates } : field))
    );
    const updatedField = fields.find((field) => field.id === id);
    if (updatedField) {
      setSelectedField({ ...updatedField, ...updates });
    }
  };

  const handleDragResize = (field, position, size) => {
    const { x, y } = position;
    const { width, height } = size;

    if (!isOverlapping(x, y, width, height, field.id, field.page)) {
      updateField(field.id, { x, y, width, height });
    }
  };
  return (
    <div
      ref={drop}
      id="doc-canvas"
      className="w-full h-full border border-gray-300 rounded-lg relative bg-gray-200 overflow-auto"
    >
      <div className="relative w-full min-h-[120vh]">
        <Document
          onLoadSuccess={({ numPages }) => setNumPages(numPages)}
          file={link}
          className="w-full h-full flex justify-center flex-col gap-2 items-center"
        >
          {numPages &&
            Array.from({ length: numPages }, (_, index) => (
              <div key={index} className="relative">
                <Page pageNumber={index + 1} width={600} />
                {fields
                  .filter((field) => field.page === index + 1)
                  .map((field) => (
                    <Rnd
                      key={field.id}
                      bounds="parent"
                      size={{ width: field.width, height: field.height }}
                      position={{ x: field.x, y: field.y }}
                      onDragStop={(e, d) =>
                        handleDragResize(
                          field,
                          { x: d.x, y: d.y },
                          { width: field.width, height: field.height }
                        )
                      }
                      onResizeStop={(e, direction, ref, delta, position) =>
                        handleDragResize(field, position, {
                          width: parseInt(ref.style.width, 10),
                          height: parseInt(ref.style.height, 10),
                        })
                      }
                      onClick={() => setSelectedField(field)}
                      className="border rounded-lg p-2 !flex items-center justify-center text-sm absolute"
                      style={{ backgroundColor: field.color || "blue" }}
                    >
                      <p className="text-xs text-center font-semibold">
                        {field.label}
                      </p>
                    </Rnd>
                  ))}
              </div>
            ))}
        </Document>
      </div>
    </div>
  );
};

const AddFieldsInDocument = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data } = location.state || {};
  const { signers, link, templateId } = data || {};
  const { id } = useParams();
  const { data: templateDetails } = useGetTemplateDetails(id);
  // ENG-1826: CP templates label which fields fill themselves; a new or copied template has only its templateId in state.
  const { data: newTemplateDetails } = useGetTemplateDetails(id ? null : templateId);
  const isCpTemplate = (templateDetails || newTemplateDetails)?.template_type === "collab_physician";
  const editFields = templateDetails?.fields;

  const users = signers
    ? signers.map((signer, index) => ({
        id: signer.signer_id,
        name: signer.name,
        role: signer.signer_role_name,
        signing_order: signer.signing_order,
        color: [
          "#eab308",
          "lightblue",
          "plum",
          "lightgreen",
          "coral",
          "lightpink",
        ][index % 6],
      }))
    : [];

  const [fields, setFields] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(users[0] || null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const { data: fieldTypes, isLoading: isLoadingFieldTypes } =
    useGetAllStandardFields();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formValues, setFormValues] = useState(fieldInitialValues);
  const [editFieldId, setEditFieldId] = useState(null);
  const {
    mutateAsync: createNewField,
    isLoading: isLoadingCreateField,
    isSuccess: isFieldCreated,
    data: fieldCreated,
  } = useCreateNewField();

  const {
    mutateAsync: updateTemplateFields,
    isLoading: isUpdatingTemplateFields,
    isSuccess: isTemplateFieldUpdated,
    data: templateFieldUpdated,
  } = useUpdateTemplateFieldsMutation();

  const { data: fieldData, isLoading: isLoadingFieldData } =
    useGetStandardFieldById(editFieldId);

  const {
    mutateAsync: updateField,
    isLoading: isLoadingUpdateField,
    isSuccess: isFieldUpdated,
    data: fieldUpdated,
  } = useUpdateField();

  const {
    mutateAsync: deleteField,
    isLoading: isLoadingDeleteField,
    isSuccess: isFieldDeleted,
    data: fieldDeleted,
  } = useDeleteField();

  const convertTemplateFieldsToCanvasFields = (templateFields) => {
    if (!templateFields || !Array.isArray(templateFields)) return [];

    return templateFields.map((field) => ({
      id: field.id || new Date().getTime(),
      field_id: field.standard_field_id || field.id,
      type: field.field_type,
      label: field.field_name || field.name,
      x: field.position_x,
      y: field.position_y,
      width: field.width,
      height: field.height,
      page: field.page_number,
      color:
        users.find((user) => user.signing_order === field.signing_order)
          ?.color || "blue",
      signing_order: field.signing_order,
    }));
  };

  useEffect(() => {
    if (editFields && editFields.length > 0) {
      const canvasFields = convertTemplateFieldsToCanvasFields(editFields);
      setFields(canvasFields);
    }
  }, [editFields]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (
      formValues.field_name.trim() === "" ||
      formValues.field_type.trim() === "" ||
      formValues.default_width.trim() === "" ||
      formValues.default_height.trim() === ""
    ) {
      toast.error("Please fill in all fields");
      return;
    }
    if (formValues.default_width <= "0" || formValues.default_height <= "0") {
      toast.error("Width or height must be greater than 0");
      return;
    }
    const payload = {
      ...formValues,
      field_type: formValues.field_type.toLowerCase(),
    };
    await createNewField(payload);
  };

  const dropdownRef = useRef(null);
  const { mutateAsync: createTemplateField, isLoading } =
    useCreateTemplateFieldMutation();
  const filteredFieldTypes = fieldTypes?.filter((field) =>
    field.field_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isOverlapping = (newX, newY, newWidth, newHeight, fieldId, page) => {
    return fields.some((f) => {
      if (f.id === fieldId || f.page !== page) return false;
      return !(
        newX + newWidth <= f.x ||
        newX >= f.x + f.width ||
        newY + newHeight <= f.y ||
        newY >= f.y + f.height
      );
    });
  };

  const handleUpdateField = async () => {
    if (
      formValues?.field_name?.trim() === "" ||
      formValues?.field_type?.trim() === "" ||
      formValues?.default_width?.toString()?.trim() === "" ||
      formValues?.default_height?.toString()?.trim() === ""
    ) {
      toast.error("Please fill in all fields");
      return;
    }
    if (
      formValues?.default_width.toString() <= "0" ||
      formValues?.default_height.toString() <= "0"
    ) {
      toast.error("Width or height must be greater than 0");
      return;
    }
    const payload = {
      ...formValues,
      id: editFieldId,
      field_type: formValues.field_type.toLowerCase(),
    };
    await updateField(payload);
  };

  const handleSettingsChange = (prop, value) => {
    const parsedValue = prop === "label" ? value : Math.max(0, Number(value));
    let updatedField = { ...selectedField, [prop]: parsedValue };

    if (
      !isOverlapping(
        updatedField.x,
        updatedField.y,
        updatedField.width,
        updatedField.height,
        updatedField.id,
        updatedField.page,
        updatedField.label
      )
    ) {
      setFields((prev) =>
        prev.map((field) =>
          field.id === selectedField.id ? updatedField : field
        )
      );
      setSelectedField(updatedField);
    }
  };

  const handleUserChange = (user) => {
    setSelectedUser(user);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const toggleSettings = () => {
    setIsSettingsOpen(!isSettingsOpen);
  };

  const handleDelete = async (id) => {
    await deleteField({ id });
  };

  useEffect(() => {
    if (isFieldCreated) {
      toast.success(fieldCreated?.message);
      setIsModalOpen(false);
      setFormValues(fieldInitialValues);
    }
    if (isTemplateFieldUpdated) {
      toast.success(templateFieldUpdated?.message);
      setIsModalOpen(false);
      setFormValues(fieldInitialValues);
    }
  }, [
    fieldCreated?.message,
    isFieldCreated,
    isTemplateFieldUpdated,
    templateFieldUpdated?.message,
  ]);

  useEffect(() => {
    if (isFieldDeleted) {
      toast.success(fieldDeleted?.message);
      setIsModalOpen(false);
      setFormValues(fieldInitialValues);
      setEditFieldId(null);
    }
  }, [fieldDeleted?.message, isFieldDeleted]);

  useEffect(() => {
    if (isFieldUpdated) {
      toast.success(fieldUpdated?.message);
      setIsModalOpen(false);
      setFormValues(fieldInitialValues);
      setEditFieldId(null);
    }
  }, [fieldUpdated?.message, isFieldUpdated]);

  useEffect(() => {
    if (editFieldId) {
      setFormValues({
        field_name: fieldData?.field_name,
        field_type: fieldData?.field_type,
        default_width: fieldData?.default_width,
        default_height: fieldData?.default_height,
      });
      setIsModalOpen(true);
    }
  }, [editFieldId, fieldData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  const renderUserDropdown = () => {
    return (
      <div className="relative inline-block text-left w-full" ref={dropdownRef}>
        {isLoadingDeleteField && <Spinner />}
        <div>
          <button
            type="button"
            className="inline-flex justify-between w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#cfc9ff]"
            id="menu-button"
            aria-expanded="true"
            aria-haspopup="true"
            onClick={toggleDropdown}
          >
            <span className="flex items-center">
              {selectedUser ? (
                <>
                  <span
                    className="inline-block h-2 w-2 mr-2 rounded-full"
                    style={{ backgroundColor: selectedUser.color }}
                  ></span>
                  {selectedUser.role}
                </>
              ) : (
                "Select User"
              )}
            </span>
            <svg
              className="-mr-1 ml-2 h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        {isDropdownOpen && (
          <div
            className="origin-top-right absolute right-0 mt-2 w-full rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10"
            role="menu"
            aria-orientation="vertical"
            aria-labelledby="menu-button"
            tabIndex="-1"
          >
            <div className="py-1" role="none">
              {users.map((user) => (
                <button
                  key={user.id}
                  onClick={() => handleUserChange(user)}
                  className={`text-gray-700 block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center ${
                    selectedUser && selectedUser.id === user.id
                      ? "bg-gray-100"
                      : ""
                  }`}
                  role="menuitem"
                  tabIndex="-1"
                  id={`menu-item-${user.id}`}
                >
                  <span
                    className="inline-block h-2 w-2 mr-2 rounded-full"
                    style={{ backgroundColor: user.color }}
                  ></span>
                  {user.role}
                  {selectedUser && selectedUser.id === user.id && (
                    <span className="ml-auto">✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleSend = async () => {
    const updatedFields = fields.map((field) => ({
      template_id: Number(templateId),
      standard_field_id: Number(field.field_id),
      field_name: field.label,
      field_type: field.type,
      position_x: field.x,
      position_y: field.y,
      height: field.height,
      width: field.width,
      signing_order: field.signing_order,
      page_number: field.page,
      name: field.label,
      id: id ? field.id : null,
    }));

    const payload = {
      templateFields: updatedFields,
    };

    if (id) {
      const res = await updateTemplateFields(payload);
      if (res?.success) {
        toast.success("Template fields updated successfully!");
        navigate("/");
      }
      if (!res?.success) {
        toast.error(res?.data?.message || "Error updating template fields");
      }
    } else {
      const res = await createTemplateField(payload);
      if (res?.success) {
        toast.success("Template fields created successfully!");
        navigate("/");
      }
      if (!res?.success) {
        toast.error(res?.data?.message || "Error creating template fields");
      }
    }
  };

  const hanldeRemoveField = (selectedField) => {
    setFields((prev) => prev.filter((field) => field.id !== selectedField.id));
    if (fields.length === 1) {
      setSelectedField(null);
    }
  };

  if (isLoadingFieldTypes) {
    return <Spinner />;
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="flex flex-col h-screen">
        <header className="bg-white text-white text-center py-6 sticky top-0 z-10 flex justify-end items-center">
          <h1 className="text-lg font-semibold text-black absolute left-1/2 -translate-x-1/2">
            Document {id ? "(Editing)" : ""}
          </h1>
          <button
            className="p-2 text-gray-600 sm:hidden"
            onClick={toggleSettings}
          >
            <FaCog />
          </button>
        </header>

        <main className="flex-grow bg-gray-50 flex overflow-hidden">
          <aside className="w-2/5 sm:w-1/5 bg-white p-3 overflow-y-auto border-r space-y-2">
            <div className="mb-4">{renderUserDropdown()}</div>

            <div className="relative mb-4">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FaSearch className="text-gray-400" />
              </div>
              <input
                type="search"
                placeholder="Search fields..."
                className="block w-full p-2 pl-10 text-sm border border-gray-300 rounded-md bg-gray-50 focus:ring-[#cfc9ff] focus:border-[#cfc9ff] dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-[#cfc9ff] dark:focus:border-[#cfc9ff]"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <h2 className="text-md font-semibold text-start pl-4">
              Standard Fields
            </h2>
            {filteredFieldTypes.map((field) => (
              <DraggableField
                key={field.id + field.field_name}
                field={field}
                setEditFieldId={setEditFieldId}
                handleDelete={handleDelete}
                cpLabel={isCpTemplate ? cpEditorLabel(field, cpSignerForOrder(selectedUser?.signing_order)) : null}
              />
            ))}

            <div
              onClick={() => {
                setIsModalOpen(true);
                setEditFieldId(null);
                setFormValues(fieldInitialValues);
              }}
              className="font-medium text-sm mt-6 ml-2 cursor-pointer rounded-lg underline"
            >
              + Create New Field
            </div>
          </aside>

          <Modal isOpen={isModalOpen} showLogo={false}>
            {isLoadingFieldData ? (
              <Spinner />
            ) : (
              <from>
                <div className="mb-4 -mt-4 flex justify-between items-center">
                  <h2 className="text-lg font-semibold">Create New Field</h2>
                  <IoIosCloseCircle
                    color="red"
                    size={24}
                    onClick={() => setIsModalOpen(false)}
                    className="cursor-pointer"
                  />
                </div>
                <div className="flex w-full gap-10 mb-4">
                  <CustomInput
                    name="field_name"
                    wrapperClassName="w-full"
                    className="h-11"
                    label="Field Name"
                    placeholder="Field Name"
                    onChange={handleChange}
                    value={formValues.field_name}
                    mandatory
                  />
                  <div className="w-full flex flex-col space-y-2">
                    <label className="text-sm font-semibold text-gray-700">
                      Field Type
                      <span className="text-red-500  ml-2">*</span>
                    </label>
                    <select
                      value={formValues.field_type}
                      name="field_type"
                      onChange={handleChange}
                      className="w-full h-11 capitalize border-gray-300 border-2 rounded-sm p-2 disabled:bg-slate-100 disabled:cursor-not-allowed text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-gray-500"
                    >
                      {inputTypes.map((type) => (
                        <option value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex w-full gap-10 mb-6">
                  <CustomInput
                    name="default_width"
                    wrapperClassName="w-full"
                    label="Width"
                    placeholder="Width"
                    className="h-11"
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      if (e.key === "e" || e.key === "E") {
                        e.preventDefault();
                      }
                    }}
                    type="number"
                    value={formValues.default_width}
                    mandatory
                  />
                  <CustomInput
                    name="default_height"
                    wrapperClassName="w-full"
                    label="Height"
                    className="h-11"
                    placeholder="Height"
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      if (e.key === "e" || e.key === "E") {
                        e.preventDefault();
                      }
                    }}
                    type="number"
                    value={formValues.default_height}
                    mandatory
                  />
                </div>
                <div className="flex justify-end">
                  <CustomButton
                    type="submit"
                    onClick={() => {
                      if (editFieldId) {
                        handleUpdateField();
                      } else {
                        handleSubmit();
                      }
                    }}
                    label={editFieldId ? "Update" : "Create"}
                    labelColor="black"
                    isLoading={
                      isLoadingCreateField ||
                      isLoadingUpdateField ||
                      isUpdatingTemplateFields
                    }
                  />
                </div>
              </from>
            )}
          </Modal>

          <div className="w-3/5 sm:w-3/5 bg-gray-50 p-4 overflow-auto">
            <DocumentCanvas
              key={JSON.stringify(filteredFieldTypes)}
              fields={fields}
              setFields={setFields}
              setSelectedField={setSelectedField}
              link={link}
              selectedUser={selectedUser}
              isOverlapping={isOverlapping}
              allFields={filteredFieldTypes}
            />
          </div>

          <aside
            className={`bg-white p-4 overflow-y-auto border-l rounded-lg shadow-sm transform transition-transform duration-300 ease-in-out fixed top-0 right-0 h-full z-20 w-full sm:w-1/5
                        ${
                          isSettingsOpen
                            ? "translate-x-0"
                            : "translate-x-full sm:translate-x-0"
                        }
                        sm:static sm:translate-x-0
            `}
          >
            <div className="flex justify-between items-center mb-4 sm:hidden">
              <h3 className="text-md font-semibold">Settings</h3>
              <button className="p-2 text-gray-600" onClick={toggleSettings}>
                <FaTimes />
              </button>
            </div>

            {selectedField ? (
              <div className="space-y-3">
                <h3 className="text-md font-semibold text-center sm:text-start">
                  Settings
                </h3>
                {["label", "x", "y", "width", "height"].map((prop) => (
                  <div key={prop}>
                    <label className="block text-xs font-medium mb-1">
                      {prop.toUpperCase()}:
                    </label>
                    <input
                      type={prop === "label" ? "text" : "number"}
                      value={selectedField[prop]}
                      onChange={(e) =>
                        handleSettingsChange(prop, e.target.value)
                      }
                      className="w-full border rounded-lg p-1 text-sm"
                    />
                  </div>
                ))}
                <button
                  onClick={() => hanldeRemoveField(selectedField)}
                  className="w-full bg-red-500 hover:bg-red-600 text-white py-1 rounded-lg text-sm"
                >
                  Remove Field
                </button>
              </div>
            ) : (
              <p className="text-center text-sm">
                Select a field to edit settings
              </p>
            )}
          </aside>
        </main>

        <footer className="bg-white text-black text-center py-2 sticky bottom-0 z-10">
          <p className="text-xs"></p>
          <button
            className="bg-[#cfc9ff] text-black disabled:bg-slate-400 disabled:cursor-not-allowed hover:bg-[#948ae3] font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline "
            onClick={handleSend}
            disabled={isLoading || fields.length === 0}
          >
            {isLoading
              ? "Saving..."
              : id
              ? "Update Template"
              : "Create Template"}
          </button>
        </footer>
      </div>
    </DndProvider>
  );
};

export default AddFieldsInDocument;
